<template>
  <el-dialog
    :title="$t('CenterForSorting.ruleConditionDetail')"
    :visible.sync="dialogVisible"
    append-to-body
    width="600px"
    @close="handleClose"
  >
    <el-row :gutter="12" class="rule-summary">
      <el-col :span="24">
        <div class="summary-item">
          <span class="summary-label">{{ $t('CenterForSorting.machineName') }}：</span>
          <span class="summary-value">{{ ruleSummary.machineName || '-' }}</span>
        </div>
      </el-col>
      <el-col :span="24">
        <div class="summary-item">
          <span class="summary-label">{{ $t('CenterForSorting.ruleGroupName') }}：</span>
          <span class="summary-value">{{ ruleSummary.ruleGroupName || '-' }}</span>
        </div>
      </el-col>
      <el-col :span="24">
        <div class="summary-item">
          <span class="summary-label">{{ $t('CenterForSorting.ruleName') }}：</span>
          <span class="summary-value">{{ ruleSummary.ruleName || '-' }}</span>
        </div>
      </el-col>
    </el-row>
    <el-table v-loading="loading" :data="localConditions" border>
      <el-table-column
        :label="$t('CenterForSorting.conditionType')"
        property="conditionType"
        width="150"
      >
        <template slot-scope="{ row }">{{ $t('CenterForSorting.' + row.conditionType) }}</template>
      </el-table-column>
      <el-table-column :label="$t('CenterForSorting.operator')" property="operator" width="100">
        <template slot-scope="{ row }">{{ $t('CenterForSorting.' + row.operator) }}</template>
      </el-table-column>
      <el-table-column :label="$t('CenterForSorting.conditionValue')" property="conditionValue">
        <template slot-scope="{ row }">
          {{ resolveValueLabel(row) }}
        </template>
      </el-table-column>
    </el-table>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">{{ $t('CenterForSorting.close') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getConditionConfigList, listConditions } from '@/api/sorting/rule';
import { waybillStatusListRule } from '@/utils/enumData';

export default {
  name: 'RuleConditionDetail',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    ruleId: {
      type: [Number, String],
      default: null
    },
    conditionConfigs: {
      type: Array,
      default: () => []
    },
    conditions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localConditions: [],
      localConditionConfigs: [],
      ruleSummary: {
        machineName: '',
        ruleGroupName: '',
        ruleName: ''
      },
      loading: false
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      }
    },
    finalConditionConfigs() {
      // 优先使用传入的配置，否则使用本地加载的
      return this.conditionConfigs.length > 0 ? this.conditionConfigs : this.localConditionConfigs;
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.initData();
      }
    }
  },
  methods: {
    async initData() {
      // 如果没有传入配置，且本地也没有加载过，则尝试加载
      if (this.conditionConfigs.length === 0 && this.localConditionConfigs.length === 0) {
        await this.fetchConditionConfigs();
      }

      // 如果有传入 conditions，直接使用
      if (this.conditions.length > 0) {
        this.localConditions = this.conditions;
        this.ruleSummary = {
          machineName: '',
          ruleGroupName: '',
          ruleName: ''
        };
      } else if (this.ruleId) {
        // 否则根据 ruleId 加载
        this.fetchConditions();
      } else {
        this.localConditions = [];
        this.ruleSummary = {
          machineName: '',
          ruleGroupName: '',
          ruleName: ''
        };
      }
    },
    async fetchConditionConfigs() {
      try {
        const res = await getConditionConfigList();
        this.localConditionConfigs = res.data || [];
      } catch (error) {
        console.error(error);
      }
    },
    fetchConditions() {
      this.loading = true;
      listConditions({ ruleId: this.ruleId })
        .then((res) => {
          const responseData = res.data || {};
          this.ruleSummary = {
            machineName: responseData.machineName || '',
            ruleGroupName: responseData.ruleGroupName || '',
            ruleName: responseData.ruleName || ''
          };
          if (Array.isArray(responseData.conditions)) {
            this.localConditions = responseData.conditions;
          } else if (Array.isArray(responseData)) {
            this.localConditions = responseData;
          } else {
            this.localConditions = [];
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    resolveValueLabel(row) {
      const config = this.finalConditionConfigs.find((c) => c.conditionType === row.conditionType);

      // 特殊处理 WAYBILL_STATUS，确保多语言切换时能实时获取最新的 label
      // 解决了切换语言后 waybillStatusList 还是旧语言的问题
      if (row.conditionType === 'WAYBILL_STATUS') {
        const statusList = waybillStatusListRule;
        const valStr = String(row.conditionValue);
        // 分割值（如果是多选）
        const values = valStr.split(',');
        const labels = values.map((v) => {
          const option = statusList.find((s) => String(s.code) === v.trim());
          return option ? option.label : v;
        });
        return labels.join(', ');
      }

      if (config) {
        // 优化：如果是 Select 类型，分割值并转换
        if (config.valueType === 'Select' && config.allowedValues) {
          const valStr = String(row.conditionValue);
          const values = valStr.split(',');
          const labels = values.map((v) => {
            const option = config.allowedValues.find((av) => String(av.value) === v.trim());
            return option ? option.label : v;
          });
          return labels.join(', ');
        }

        // 兼容旧逻辑（如果 valueType 不是 Select 或者没有 allowedValues，但可能通过其他方式匹配）
        if (config.allowedValues) {
          const valStr = String(row.conditionValue);
          const option = config.allowedValues.find((v) => String(v.value) === valStr);
          if (option) return option.label;
        }
      }

      return row.conditionValue;
    },
    handleClose() {
      this.dialogVisible = false;
    }
  }
};
</script>

<style scoped>
.rule-summary {
  margin-bottom: 12px;
}

.summary-item {
  background: #f5f7fa;
  border-radius: 4px;
  padding: 8px 10px;
  line-height: 20px;
}

.summary-label {
  color: #909399;
}

.summary-value {
  color: #303133;
}
</style>
